import React, { FC, memo, useMemo } from 'react';
import { backgroundImageForUrl } from '@shared/utils/style/backgroundImage';
import { Box, Flex } from '@withjoy/joykit';
import { addRendition } from '@shared/utils/photoRendition';
import { pxToRem } from '@withjoy/joykit/theme';
import { LazyScreen } from '../LazyScreen/LazyScreen';
import { AnimatedImageBox } from '../PhotoTrio/PhotoTrio.styles';
import { ArchPhotoProps, PhotoArchProps } from './PhotoArch.types';
import { CroppedImage } from '@shared/components/MultiPhotoSelector/components/CroppedImage/CroppedImage';
import { useGetContainerDimensions } from '../../hooks/useGetContainerDimensions';

const MAX_HEIGHT = 600;
const ASPECT_RATIO = 9 / 7;
const OUTER_PIECE_THICKNESS = 2.88;
const INNER_GAP_THICKNESS_Y = 23;
const INNER_GAP_THICKNESS_X = 17;

const ArchPhoto: FC<ArchPhotoProps> = props => {
  const { photo, containerDimensions, calculateBorderRadius, outerArchColor, innerArchColor } = props;
  if (photo.cropInfo) {
    const height = containerDimensions.height;
    const width = height * ASPECT_RATIO;
    return (
      <AnimatedImageBox
        display="flex"
        position="relative"
        width="auto"
        height="100%"
        borderTopLeftRadius={pxToRem(calculateBorderRadius)}
        borderTopRightRadius={pxToRem(calculateBorderRadius)}
        aspectRatio={`${ASPECT_RATIO}`}
        overflow="hidden"
        border={`${pxToRem(OUTER_PIECE_THICKNESS)} solid`}
        borderColor={outerArchColor}
      >
        <CroppedImage
          cropInfo={{ ...photo.cropInfo, unit: '%' }}
          imageUrl={addRendition({ url: photo.url, renditionSize: 'medium' })}
          height={height}
          width={width}
        />
        <Box
          position="absolute"
          inset={0}
          backgroundColor="transparent"
          borderTop={pxToRem(INNER_GAP_THICKNESS_X)}
          borderRight={pxToRem(INNER_GAP_THICKNESS_Y)}
          borderBottom={pxToRem(INNER_GAP_THICKNESS_X)}
          borderLeft={pxToRem(INNER_GAP_THICKNESS_Y)}
          borderStyle="solid"
          borderColor={innerArchColor}
          borderTopLeftRadius={pxToRem(calculateBorderRadius)}
          borderTopRightRadius={pxToRem(calculateBorderRadius)}
        />
      </AnimatedImageBox>
    );
  }
  return (
    <AnimatedImageBox
      position="relative"
      width="auto"
      height="100%"
      borderTopLeftRadius={pxToRem(calculateBorderRadius)}
      borderTopRightRadius={pxToRem(calculateBorderRadius)}
      aspectRatio={`${ASPECT_RATIO}`}
      backgroundImage={backgroundImageForUrl(addRendition({ url: photo.url, renditionSize: 'medium' }))}
      backgroundSize="cover"
      backgroundRepeat="no-repeat"
      backgroundPosition="center"
      border={`${pxToRem(OUTER_PIECE_THICKNESS)} solid`}
      borderColor={outerArchColor}
    >
      <Box
        position="absolute"
        inset={0}
        backgroundColor="transparent"
        borderTop={pxToRem(INNER_GAP_THICKNESS_X)}
        borderRight={pxToRem(INNER_GAP_THICKNESS_Y)}
        borderBottom={pxToRem(INNER_GAP_THICKNESS_X)}
        borderLeft={pxToRem(INNER_GAP_THICKNESS_Y)}
        borderStyle="solid"
        borderColor={innerArchColor}
        borderTopLeftRadius={pxToRem(calculateBorderRadius)}
        borderTopRightRadius={pxToRem(calculateBorderRadius)}
      />
    </AnimatedImageBox>
  );
};

export const PhotoArch: FC<PhotoArchProps> = memo(props => {
  const { photo, showSkeleton, innerArchColor = 'white', outerArchColor = 'mono14', showPlaceholder = false, isFullHeight = true } = props;

  const { containerDimensions, containerRef } = useGetContainerDimensions();

  const calculateBorderRadius = useMemo(() => {
    const maxHeight = photo && containerDimensions.height ? Math.min(MAX_HEIGHT, containerDimensions.height) : MAX_HEIGHT;
    const width = maxHeight * ASPECT_RATIO;
    return width / 2 + OUTER_PIECE_THICKNESS;
  }, [containerDimensions.height, photo]);

  return (
    <Flex
      width="100%"
      height={isFullHeight ? '100%' : 'auto'}
      maxHeight={MAX_HEIGHT}
      aspectRatio={`${ASPECT_RATIO}`}
      alignItems="center"
      justifyContent="center"
      ref={containerRef}
    >
      {!photo ? (
        showPlaceholder ? (
          <Box
            width="80%"
            height="100%"
            backgroundColor="lightgray"
            borderTopLeftRadius={pxToRem(calculateBorderRadius)}
            borderTopRightRadius={pxToRem(calculateBorderRadius)}
          />
        ) : null
      ) : (
        <LazyScreen
          key={photo.id}
          src={addRendition({ url: photo.url, renditionSize: 'medium' })}
          skeletonGroupOverrides={{
            Root: {
              props: {
                width: 'auto',
                height: '100%',
                maxHeight: MAX_HEIGHT,
                aspectRatio: `${ASPECT_RATIO}`,
              },
            },
            PlaceholderRoot: {
              props: {
                width: 'auto',
                height: '100%',
                maxHeight: MAX_HEIGHT,
                aspectRatio: `${ASPECT_RATIO}`,
              },
            },
          }}
          width="100%"
          height="100%"
          aspectRatio={`${ASPECT_RATIO}`}
          showSkeleton={showSkeleton}
        >
          {() => (
            <ArchPhoto
              photo={photo}
              containerDimensions={containerDimensions}
              calculateBorderRadius={calculateBorderRadius}
              outerArchColor={outerArchColor}
              innerArchColor={innerArchColor}
            />
          )}
        </LazyScreen>
      )}
    </Flex>
  );
});

PhotoArch.displayName = 'PhotoArch';
